/**
 * Barre d'actions consolidée d'un OR (extrait de OrDetail.jsx — iter31, refonte iter92).
 *
 * Refonte iter92 : ON NE FACTURE PLUS DEPUIS L'OR.
 *   - "Travaux terminés" clôture le dossier → file "Factures à émettre" (Facturation)
 *   - L'émission (Caisse / Mail / Lixt / Courrier / Hybride) et les ré-envois se font
 *     dans Facturation. L'OR affiche seulement des liens de navigation.
 *
 * Regroupe :
 *   - Action rapide (select : travaux terminés / transitions / sauts directs)
 *   - Bouton "Annuler l'OR" + Archiver / Désarchiver
 *   - Liens vers Facturation (éditer & émettre / voir la facture émise)
 *   - Actions Devis (Aperçu PDF / Télécharger / Envoyer SMS au client)
 *   - Actions Facture PDF (lecture seule : Aperçu QR-bill / Télécharger)
 */
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  FileText, Download, Mail, MessageSquare, MessageCircle, Globe, Loader2, Archive, ArchiveRestore, Receipt,
  ChevronRight, CalendarClock,
} from "lucide-react";

const A_RECUPERER = ["pret", "facture", "facturee", "paye"];

export default function OrActionsBar({
  or,
  nextStatuts,
  skippableTargets,
  canBillRoles,
  canPreviewPdf,
  canSendDevis,
  canShowFacture,
  sendingSms,
  statutLabels,
  // callbacks
  changeStatut,
  setFinalDialog,
  archiveOr,
  desarchiveOr,
  openPdfInTab,
  downloadPdf,
  sendDevisSms,
  sendDevisWhatsapp,
  notifierPretWhatsapp,
  copyDevisPublicLink,
  openFacturePdfInTab,
  downloadFacturePdf,
  onSendOrEmail,
  markEnleve,
  onPlanifier,
  planifieDate,
  planifieHeure,
}) {
  const navigate = useNavigate();
  // iter127 — OR Parc : pas de finalisation classique ni d'émission individuelle.
  // La facturation se fait EN GROUPE via /facturation → onglet "Parc".
  const isParc = !!or.is_parc;
  // iter244 — la révision du devis se lit à côté des icônes d'envoi (plus de libellé long)
  const revision = (or.devis_revision || 1) + 1;
  const isRenvoi = ["approbation", "commencement", "travaux_effectue"].includes(or.statut);
  const smsTitle = or.statut === "devis_envoye"
    ? "Renvoyer le devis par SMS"
    : isRenvoi ? `Renvoyer le devis modifié par SMS (v${revision})` : "Envoyer le devis par SMS";

  const hasStatusActions =
    nextStatuts.length > 0 ||
    skippableTargets.length > 0 ||
    or.statut === "travaux_effectue" ||
    or.statut === "pret" ||
    ["facture", "paye", "annule"].includes(or.statut) ||
    or.archive;

  return (
    <>
      {hasStatusActions && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap">
          {/* Action principale */}
          {(nextStatuts.length > 0 ||
            skippableTargets.length > 0 ||
            A_RECUPERER.includes(or.statut) ||
            (or.statut === "travaux_effectue" && !isParc)) && (
            <Select
              value=""
              onValueChange={(v) => {
                if (!v) return;
                if (v === "__finaliser__") setFinalDialog(true);
                else if (v === "__enleve__") markEnleve?.(!or.enleve_at);
                else changeStatut(v);
              }}
            >
              <SelectTrigger
                className="h-9 w-full sm:w-52 bg-[#52a675] hover:bg-[#3d8a5c] text-white border-0 font-medium"
                data-testid="or-action-rapide"
              >
                <SelectValue placeholder="⚡ Action rapide…" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {or.statut === "travaux_effectue" && !isParc && (
                  <SelectItem value="__finaliser__" data-testid="or-action-finaliser">
                    ✓ Travaux terminés — clôturer
                  </SelectItem>
                )}
                {/* iter258 — confirmation de l'enlèvement du véhicule par le client */}
                {A_RECUPERER.includes(or.statut) && (
                  <SelectItem value="__enleve__" data-testid="or-action-enleve">
                    {or.enleve_at ? "↺ Annuler « véhicule enlevé »" : "🔑 Véhicule enlevé par le client"}
                  </SelectItem>
                )}
                {nextStatuts.filter((s) => s !== "annule").map((s) => (
                  <SelectItem key={s} value={s} data-testid={`or-action-${s}`}>
                    → {statutLabels[s]}
                  </SelectItem>
                ))}
                {skippableTargets.length > 0 && (
                  <>
                    <div className="my-1 border-t border-gray-100" />
                    <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-gray-400">
                      Saut direct
                    </div>
                    {skippableTargets.map((s) => (
                      <SelectItem key={`skip-${s}`} value={s} data-testid={`or-action-skip-${s}`}>
                        ↷ {statutLabels[s]}
                      </SelectItem>
                    ))}
                  </>
                )}
                {/* iter252 — « Annuler l'OR » déplacé ici : c'est un changement de statut,
                    il n'a plus de bouton dédié à côté du menu. */}
                {nextStatuts.includes("annule") && (
                  <>
                    <div className="my-1 border-t border-gray-100" />
                    <SelectItem
                      value="annule"
                      className="text-red-600 focus:text-red-700 focus:bg-red-50"
                      data-testid="or-statut-annule"
                    >
                      ✕ Annuler l&apos;OR
                    </SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          )}

          {/* iter127 — OR Parc terminé : lien direct vers Facturation Parc */}
          {isParc && or.statut === "travaux_effectue" && !or.facture_groupee_id && (
            <Button
              size="sm"
              onClick={() => navigate(`/facturation`, { state: { tab: "parc", clientId: or.client_id } })}
              className="bg-[#52a675] hover:bg-[#3d8a5c] text-white"
              data-testid="or-parc-goto-facturation"
              title="Cet OR Parc sera facturé en GROUPE avec les autres OR du même client (Facturation → Parc)"
            >
              <Receipt className="w-3.5 h-3.5 mr-1.5" /> Voir dans Facturation Parc
              <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          )}

          {/* iter127 — Badge informatif quand OR Parc déjà groupé */}
          {isParc && or.facture_groupee_id && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/facturation/${or.facture_groupee_id}`)}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="or-parc-facture-groupee"
            >
              <Receipt className="w-3.5 h-3.5 mr-1.5" />
              Facture groupée émise — voir
            </Button>
          )}

          {/* iter364 — Prévenir le client par WhatsApp que son véhicule est prêt */}
          {["travaux_effectue", "pret"].includes(or.statut) && notifierPretWhatsapp && (
            <Button
              size="sm" onClick={notifierPretWhatsapp} disabled={sendingSms}
              className="bg-[#25D366] hover:bg-[#128C7E] text-white"
              data-testid="or-pret-whatsapp"
              title="Prévenir le client par WhatsApp que le véhicule est prêt"
            >
              {sendingSms
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <MessageCircle className="w-3.5 h-3.5 mr-1.5" />}
              Véhicule prêt · WhatsApp
            </Button>
          )}

          {/* iter92 — la facturation se fait dans Facturation : lien d'accès direct
              iter127 — Caché pour OR Parc (facturation groupée via /facturation Parc) */}
          {or.statut === "pret" && !or.facture_id && canBillRoles && !isParc && (
            <Button
              size="sm"
              onClick={() => navigate(`/facturation/a-emettre/${or.id}`)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="or-goto-emission"
              title="Éditer la facture (lignes, client, véhicule) puis choisir le canal d'émission"
            >
              <Receipt className="w-3.5 h-3.5 mr-1.5" /> Éditer & émettre la facture
              <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          )}

          {or.statut === "pret" && or.facture_id && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/facturation/${or.facture_id}`)}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="or-goto-facture"
            >
              <Receipt className="w-3.5 h-3.5 mr-1.5" />
              Facture {or.facture_numero} émise — voir dans Facturation
            </Button>
          )}

          {!or.archive && ["facture", "paye", "annule", "pret", "travaux_effectue"].includes(or.statut) && (
            <Button
              size="sm"
              variant="outline"
              onClick={archiveOr}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
              data-testid="or-archive-btn"
            >
              <Archive className="w-3.5 h-3.5 mr-1.5" /> Archiver
            </Button>
          )}

          {or.archive && (
            <Button
              size="sm"
              variant="outline"
              onClick={desarchiveOr}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="or-desarchive-btn"
            >
              <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" /> Désarchiver
            </Button>
          )}
        </div>
      )}

      {!canBillRoles && (or.statut === "travaux_effectue" || or.statut === "pret" || or.statut === "facture") && (
        <div
          className="mt-3 pt-3 border-t border-gray-100 text-xs bg-amber-50 border border-amber-200 rounded p-2.5 text-amber-900"
          data-testid="or-role-warning"
        >
          ⓘ La facturation et l&apos;encaissement se font dans le module Facturation (administration / comptoir).
        </div>
      )}

      {/* Actions devis — iter247 : tout sur une ligne, agenda aligné à droite */}
      {(canPreviewPdf || canSendDevis || onPlanifier) && (
        <div className="mt-2.5 pt-2.5 border-t border-gray-100 flex items-center gap-1.5 flex-wrap">
          <span className="text-xs uppercase tracking-wider text-gray-400 mr-1">Devis</span>
          {canPreviewPdf && (
            <>
              <Button size="sm" variant="outline" onClick={openPdfInTab} data-testid="or-devis-preview">
                <FileText className="w-3.5 h-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Aperçu</span>
              </Button>
              <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="or-devis-download">
                <Download className="w-3.5 h-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Télécharger</span>
              </Button>
            </>
          )}
          {canSendDevis && (
            <>
              <Button
                size="icon" variant="outline" onClick={onSendOrEmail} disabled={!onSendOrEmail}
                className="h-9 w-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="or-send-email"
                title="Envoyer le devis par email"
                aria-label="Envoyer le devis par email"
              >
                <Mail className="w-4 h-4" />
              </Button>
              <Button
                size="icon" onClick={sendDevisSms} disabled={sendingSms}
                className="h-9 w-9 bg-[#0891b2] hover:bg-[#0e7490]"
                data-testid="or-devis-send-sms"
                title={smsTitle}
                aria-label={smsTitle}
              >
                {sendingSms
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <MessageSquare className="w-4 h-4" />}
              </Button>
              <Button
                size="icon" onClick={sendDevisWhatsapp} disabled={sendingSms || !sendDevisWhatsapp}
                className="h-9 w-9 bg-[#25D366] hover:bg-[#128C7E] text-white"
                data-testid="or-devis-send-whatsapp"
                title="Envoyer le devis par WhatsApp"
                aria-label="Envoyer le devis par WhatsApp"
              >
                {sendingSms
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <MessageCircle className="w-4 h-4" />}
              </Button>
              <Button
                size="icon" variant="outline" onClick={copyDevisPublicLink}
                className="h-9 w-9 border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="or-devis-copy-link"
                title="Copier le lien public du devis (WhatsApp, etc.)"
                aria-label="Copier le lien public du devis"
              >
                <Globe className="w-4 h-4" />
              </Button>
            </>
          )}
          {onPlanifier && (
            <Button
              size="icon" variant="outline" onClick={onPlanifier}
              className="h-9 w-9 ml-auto border-[#52a675]/40 text-[#2d5f3f] hover:bg-[#52a675]/10"
              data-testid="or-planifier-btn"
              title={planifieDate
                ? `Replanifier — actuellement le ${planifieDate}${planifieHeure ? ` à ${planifieHeure}` : ""}`
                : "Planifier dans l'agenda"}
              aria-label="Planifier dans l'agenda"
            >
              <CalendarClock className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}

      {/* PDF facture (lecture) — les envois se font depuis Facturation */}
      {canShowFacture && (
        <div
          className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap"
          data-testid="or-facture-actions"
        >
          <span className="text-xs text-gray-500 mr-1">
            Facture{" "}
            {or.facture_numero ? (
              <code className="font-mono text-gray-800">{or.facture_numero}</code>
            ) : (
              ""
            )}{" "}
            :
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={openFacturePdfInTab}
            data-testid="or-facture-preview"
            className="border-[#52a675]/40 text-[#2d5f3f] hover:bg-[#52a675]/5"
          >
            <FileText className="w-3.5 h-3.5 mr-1.5" /> Aperçu facture (QR-bill)
          </Button>
          <Button
            size="sm"
            onClick={downloadFacturePdf}
            data-testid="or-facture-download"
            className="bg-[#52a675] hover:bg-[#3d8a5c]"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> Télécharger
          </Button>
        </div>
      )}
    </>
  );
}
